import React from "react";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
//#region types
import { TranslationKeys } from "translations";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
import { WintSuperkollAPIModelCustomFilters } from "wint-api-client/dist/types";
//#endregion
//#region components
import { Grid } from "@mui/material";
import useCompanyBooksReportFilterDefinition from "components/accounting/forms/useCBReportFilterDefinition";
import TransactionsList, { TransactionsListHandle } from "components/accounting/companyBooks/list";
import useCompanyBooksExport from "components/accounting/companyBooks/hooks/useCompanyBooksExport";
import {
  DashboardButtonGroup,
  FilterButton,
  PageLayouts,
  ReportSearchInput,
  SearchContextProvider,
  StyledButton,
} from "components/common";
import { useCreateVoucherDialog, useVoucherPermissions } from "components/voucher";
import { useTransactionService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import { IconDefinitionConstants } from "utils/constants";
import ExportLoadingMessage from "components/common/messages/exportLoadingMessage";
import { StyleEnums } from "models/enums";
import useFloatingMessages from "components/common/messages/useFloatingMessages";

const CompanyBooksReport: React.FC = (props) => {
  const transactionService = useTransactionService();
  const dispatch = useDispatch();
  const { filterFormFields, filterQueryStateHandler, apiToReportFilterFormatter } =
    useCompanyBooksReportFilterDefinition();
  const loadingMessage = <ExportLoadingMessage />;
  const { enqueueMessage, closeMessage } = useFloatingMessages();

  const createVoucherDialog = useCreateVoucherDialog();
  const onVoucherCreateClick = () => {
    dispatch(displayDialog(createVoucherDialog));
  };

  const { getCanBookVouchers } = useVoucherPermissions();
  const tableRef = React.useRef<TransactionsListHandle>(null);
  const { onExportReportCSV, onExportReportExcel } = useCompanyBooksExport();

  const onExportReportExcelClick = async () => {
    const messageKey = enqueueMessage(loadingMessage, StyleEnums.DisplayType.Info);
    const items = (await tableRef.current?.exportAllItems()) ?? [];
    await onExportReportExcel(items);
    closeMessage(messageKey);
  };

  const onExportReportCSVClick = async () => {
    const messageKey = enqueueMessage(loadingMessage, StyleEnums.DisplayType.Info);
    const items = (await tableRef.current?.exportAllItems()) ?? [];
    await onExportReportCSV(items);
    closeMessage(messageKey);
  };
  const getTopRightButtons = () => {
    let buttons: (React.ReactElement | typeof ACTION_SEPARATOR)[] = [
      <ReportSearchInput placeholder={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Search)} />,
      ACTION_SEPARATOR,
    ];

    if (getCanBookVouchers()) {
      buttons = [
        ...buttons,
        <StyledButton size="sm" onClick={onVoucherCreateClick}>
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Voucher.CreateVoucher)}
        </StyledButton>,
        ACTION_SEPARATOR,
      ];
    }
    buttons = [...buttons, <FilterButton />];

    return buttons;
  };

  return (
    <SearchContextProvider>
      <PageLayouts.DashboardLayout
        entityKey={0}
        title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Accounting)}
        topRightButtons={
          <DashboardButtonGroup
            buttons={getTopRightButtons()}
            moreActions={[
              {
                description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ExportToExcel),
                icon: IconDefinitionConstants.WSave,
                onClickHandler: onExportReportExcelClick,
              },
              {
                description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ExportToCSV),
                icon: IconDefinitionConstants.WSave,
                onClickHandler: onExportReportCSVClick,
              },
            ]}
          />
        }
        filterFields={filterFormFields}
        filterQueryStateHandler={filterQueryStateHandler}
        apiToReportFilterFormatter={apiToReportFilterFormatter}
        reportToAPIFilterFormatter={transactionService.reportToAPIFilter}
        filterCategory={WintSuperkollAPIModelCustomFilters.FilterCategory.Transaction}
      >
        <Grid item container>
          <TransactionsList ref={tableRef} />
        </Grid>
      </PageLayouts.DashboardLayout>
    </SearchContextProvider>
  );
};

export default CompanyBooksReport;
